import {
  BootstrapperBase,
  LogService,
  TOKENS,
} from '@trustedshops/tswp-core-common';
import { FeatureBookingBootstrapper } from './feature-booking.bootstrapper';
import { PermissionsBootstrapper } from './permissions.bootstrapper';
import { ProductFeatureBootstrapper } from './product-feature.bootstrapper';
import { APP_INITIALIZER, Inject, Injectable, Optional } from '@angular/core';
import {
  ErrorCode,
  ErrorService,
  SystemCriticalError,
} from '@trustedshops/tswp-carrier-core';
import { IDENTITY_INIT_COMPLETE } from '@trustedshops/tswp-core-common-diag-instana';

@Injectable()
export class SystemCriticalBootstrapper implements BootstrapperBase {
  private readonly TYPE: string = 'SystemCriticalBootstrapper';

  public constructor(
    @Inject(TOKENS.LogService)
    private readonly _logService: LogService,
    private readonly _errorService: ErrorService,
    private readonly _permissionsBootstrapper: PermissionsBootstrapper,
    private readonly _featureBookingBootstrapper: FeatureBookingBootstrapper,
    private readonly _productFeatureBootstrapper: ProductFeatureBootstrapper,
    @Inject(IDENTITY_INIT_COMPLETE)
    @Optional()
    private readonly _identityInitPromise?: Promise<void>
  ) {}

  public async initialize(): Promise<void> {
    // Handle identity initialization errors if identity init promise is available
    if (this._identityInitPromise) {
      try {
        await this._identityInitPromise;
      } catch (error) {
        const systemError = SystemCriticalError.forService(
          error as Error,
          ErrorCode.IdentityError
        );
        this._logService.error(this.TYPE, systemError, {
          source: 'host-application',
          context: 'system-critical-initialization',
          bootstrapper: 'IdentityBootstrapper',
          errorCode: ErrorCode.IdentityError,
        });

        // Activate error state so components can display error UI
        this._errorService.activateErrorState(systemError);

        // Throw to reject the APP_INITIALIZER promise, allowing error handling in main.ts
        throw systemError;
      }
    }

    const initializers: [BootstrapperBase, ErrorCode][] = [
      [this._permissionsBootstrapper, ErrorCode.PermissionError],
      [this._featureBookingBootstrapper, ErrorCode.FeatureBookingError],
      [this._productFeatureBootstrapper, ErrorCode.ProductFeatureError],
    ];

    // Start every bootstrapper now, in the declared order. The network waits
    // then overlap instead of running one after the other — the serial form
    // put the feature-booking fetch in front of the product-feature fetch and
    // delayed the plugin fan-out by the full booking round trip.
    const started: [Promise<void>, BootstrapperBase, ErrorCode][] =
      initializers.map(([initializer, errorCode]) => {
        this._logService.trace(
          this.TYPE,
          `Initializing ${initializer.constructor.name}`
        );
        // The async wrapper turns a synchronous throw into a rejection, so the
        // error attribution below stays correct.
        const operation = (async () => initializer.initialize())();
        return [operation, initializer, errorCode];
      });

    // Mark every rejection as handled. Without this, a failure of an early
    // bootstrapper leaves the later, not-yet-awaited promises as unhandled
    // rejections.
    for (const [operation] of started) {
      operation.catch(() => undefined);
    }

    // Await in the declared order and keep the first-failure-throws semantics.
    // Difference to the serial form: a later bootstrapper has already started
    // when an earlier one fails — its request is wasted, nothing more.
    for (const [operation, initializer, errorCode] of started) {
      try {
        await operation;
        // Logged on completion, not on start: all three start in the same tick
        // now, so only this line carries per-stage timing.
        this._logService.trace(
          this.TYPE,
          `Initialized ${initializer.constructor.name}`
        );
      } catch (error) {
        const systemError = SystemCriticalError.forService(
          error as Error,
          errorCode
        );
        this._logService.error(this.TYPE, systemError, {
          source: 'host-application',
          context: 'system-critical-initialization',
          bootstrapper: initializer.constructor.name,
          errorCode: errorCode,
        });

        // Activate error state so components can display error UI
        this._errorService.activateErrorState(systemError);

        // Throw to reject the APP_INITIALIZER promise, allowing error handling in main.ts
        throw systemError;
      }
    }
  }
}

export const SystemCriticalBootstrapperInitializer = [
  PermissionsBootstrapper,
  FeatureBookingBootstrapper,
  ProductFeatureBootstrapper,
  SystemCriticalBootstrapper,
  {
    provide: APP_INITIALIZER,
    multi: true,
    useFactory: (initializer: SystemCriticalBootstrapper) => () =>
      initializer.initialize(),
    deps: [SystemCriticalBootstrapper],
  },
];
